import { PracticeQueryParams } from 'constants/application-constants'

import { useEffect } from 'react'

import clsx from 'clsx'
import {
  Card,
  LoadingIndicator,
  PageTitle,
  Section,
  SectionHeader,
} from 'components/common'
import { DocumentsTable } from 'components/features/Documents/components'
import { permissionsActions } from 'components/features/Other/api/permissionsSlice'
import {
  useAppDispatch,
  useAppSelector,
  usePermissions,
  useToggle,
} from 'hooks'
import { t } from 'i18next'
import { useParams, useSearchParams } from 'react-router-dom'

import EmptyDocumentsList from './EmptyDocumentsList'
import MainDocumentCard from './MainDocumentCard'
import MetadataDetailCard from './MetadataDetailCard'
import styles from './PracticeInformation.module.scss'
import {
  currentDocumentsSelector,
  removeMainDocument,
  setDocuments,
  setMainDocument,
} from '../../../Documents/api/currentDocumentsSlice'
import {
  practicesApi,
  selectCurrentRevision,
  setCurrentRevision,
} from '../../api'
import { selectMinimalPractice } from '../../api/minimalPracticeSlice'
import PracticeSidebar from '../PracticeSidebar/PracticeSidebar'

const PracticeInformationFrontOffice = () => {
  const dispatch = useAppDispatch()
  const [isModalOpen, toggleSetOutdated] = useToggle(false)
  const currentRevision = useAppSelector(selectCurrentRevision)
  const currentMainDocument = useAppSelector(
    currentDocumentsSelector.selectMainDocument
  )
  const currentDocuments = useAppSelector(
    currentDocumentsSelector.selectCurrentDocuments
  )

  const practice = useAppSelector(selectMinimalPractice)

  const [searchParams] = useSearchParams()
  const { id = '' } = useParams()

  // TODO: move active language information to store
  const activeLanguageCode =
    searchParams.get(PracticeQueryParams.METADATA_LANGUAGE) ||
    currentRevision?.metadata[0].language.code

  const currentMetadata = currentRevision?.metadata.find(
    ({ language }) => activeLanguageCode === language.code
  )

  const httpGetExistingPractices =
    practicesApi.useGetExistingPracticesQuery(null)
  const httpGetPracticeState =
    practicesApi.endpoints.getMinimalPracticeById.useQueryState(id)

  usePermissions(id) // Ensure permissions for guideline get fetched

  useEffect(() => {
    dispatch(permissionsActions.setEnforcedGuideline(!!id ? id : null))

    return () => {
      dispatch(permissionsActions.setEnforcedGuideline(null))
    }
  }, [dispatch, id])

  useEffect(() => {
    if (
      !httpGetPracticeState.isFetching &&
      !httpGetPracticeState.isError &&
      httpGetPracticeState.isSuccess
    ) {
      // set current revision
      if (
        !!httpGetPracticeState.data?.data?.latest_published_revision &&
        !!currentRevision?.status
      ) {
        dispatch(
          setCurrentRevision(
            httpGetPracticeState.data.data.latest_published_revision
          )
        )
      }

      // set main document
      if (
        !!httpGetPracticeState.data?.data?.latest_published_revision
          ?.main_document &&
        httpGetPracticeState.data.data.latest_published_revision.main_document
          .length > 0 &&
        !!currentRevision?.status
      ) {
        dispatch(
          setMainDocument(
            httpGetPracticeState.data.data.latest_published_revision
              .main_document[0]
          )
        )
      } else {
        dispatch(removeMainDocument())
      }

      // set documents
      if (
        !!httpGetPracticeState?.data?.data?.latest_published_revision?.documents
          ?.length
      ) {
        dispatch(
          setDocuments(
            httpGetPracticeState.data.data.latest_published_revision.documents
          )
        )
      } else {
        dispatch(setDocuments([]))
      }
    }
  }, [
    httpGetPracticeState.isFetching,
    httpGetPracticeState.isError,
    httpGetPracticeState.isSuccess,
    httpGetPracticeState.data,
    currentRevision?.status,
    dispatch,
  ])

  const isLoading = httpGetPracticeState.isFetching
  const isError = !isLoading && httpGetPracticeState.isError

  if (isLoading) {
    return (
      <div className={styles.loader}>
        <LoadingIndicator />
      </div>
    )
  }

  // ERROR/ EMPTY STATE
  if (!isError && !currentMetadata) {
    return null
  }

  if (isError) {
    // @ts-ignore
    const practiceErrorStatus = httpGetPracticeState?.error?.status
    // @ts-ignore
    const revisionErrorStatus = httpGetRevision?.error?.status

    return (
      <div className={styles.root}>
        <div className={styles.body}>
          <Section>
            <PageTitle title={t('practice.loadingErrorTitle')} />
            {(practiceErrorStatus === 404 || revisionErrorStatus === 404) && (
              <div>{t('practice.loadingError404')}</div>
            )}
            {practiceErrorStatus !== 404 && revisionErrorStatus !== 404 && (
              <div>{t('practice.loadingError')}</div>
            )}
          </Section>
        </div>
      </div>
    )
  }

  const data = httpGetPracticeState.data?.data

  var mainDocMetadata = null

  if (currentMainDocument) {
    let temp
    const mainDocument = currentMainDocument
    temp = mainDocument && mainDocument['latest_approved_version']?.metadata
    mainDocMetadata = temp && 0 in temp ? temp[0] : undefined
  }

  const wrapperCss = clsx({ [styles.wrapper]: false })

  return (
    <div className={styles.root}>
      <div className={styles.body}>
        {currentMetadata && (
          <Section>
            <PageTitle
              title={currentMetadata.title}
              description={currentMetadata.subject}
            />
            <div className={wrapperCss}>
              {practice && data && httpGetExistingPractices.isSuccess && (
                <MetadataDetailCard
                  metadata={currentMetadata}
                  author={data.author}
                  practice={practice}
                  practices={httpGetExistingPractices.data.data}
                  isModalOpen={isModalOpen}
                  toggleModal={toggleSetOutdated}
                />
              )}
            </div>
          </Section>
        )}

        {!httpGetPracticeState.isFetching && httpGetPracticeState.isSuccess && (
          <>
            {mainDocMetadata && currentMainDocument?.id && (
              <Section>
                <SectionHeader title={t('practice.mainDocument')} />

                <MainDocumentCard
                  document={mainDocMetadata}
                  docId={currentMainDocument.id}
                  guidelineId={id}
                />
              </Section>
            )}

            {currentDocuments && currentDocuments.length > 0 && (
              <Section>
                <SectionHeader title={t('practice.attachments')} />
                <Card hasTable>
                  <Card.Body className={styles.docBody}>
                    <DocumentsTable
                      documents={currentDocuments}
                      status={'published'}
                      guidelineId={id}
                    />
                  </Card.Body>
                </Card>
              </Section>
            )}

            {(!currentDocuments || currentDocuments.length === 0) &&
              !mainDocMetadata && <EmptyDocumentsList />}
          </>
        )}
      </div>
      <PracticeSidebar />
    </div>
  )
}

export default PracticeInformationFrontOffice
